/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable no-use-before-define */
import { Indexed, isBoolean, isFunction, isNotPresent, isNumber, isPresent, isString } from '../utils';
import { Lock, Response, Status } from './types';

export interface HttpOptions {
  method: 'GET' | 'POST' | 'DELETE' | 'OPTIONS';
  url: string;
  params?: HttpParams;
  headers?: Indexed<string>;
  body?: any;
  withCredentials?: boolean;
  timeout?: number;
  retries?: number;
  lock: Lock;
}

export type HttpParam = string | number | boolean | undefined;

export class HttpParams {
  private readonly content: Indexed<string>;

  constructor(params: Indexed<HttpParam> = {}) {
    this.content = Object.keys(params).reduce((acc: Indexed<string>, name: string) => {
      const value: HttpParam = params[name];
      if (isNotPresent(value)) {
        acc[name] = value;
      }
      if (isString(value) || isNumber(value) || isBoolean(value)) {
        acc[name] = String(value);
      }
      return acc;
    }, {});
  }

  set(name: string, value?: HttpParam): HttpParams {
    if (isNotPresent(value)) {
      this.content[name] = value;
    }
    if (isString(value) || isNumber(value) || isBoolean(value)) {
      this.content[name] = String(value);
    }
    return this;
  }

  setAll(params: Indexed<HttpParam>): HttpParams {
    Object.keys(params).forEach((name: string) => this.set(name, params[name]));
    return this;
  }

  get(name: string): string | undefined {
    return this.content[name];
  }

  delete(name: string): HttpParams {
    delete this.content[name];
    return this;
  }

  toString(): string {
    return `?${Object.keys(this.content)
      .reduce((acc: string[], name: string) => {
        const value = this.content[name];
        if (isPresent(value)) {
          return acc.concat(`${name}=${value}`);
        }
        return acc.concat(name);
      }, [])
      .join('&')}`;
  }
}

export const httpRequest = <T>({
  method,
  url,
  params,
  headers = { Accept: 'application/json' },
  body,
  withCredentials = true,
  timeout,
  retries = 0,
  lock,
}: HttpOptions): Promise<Response<T>> => {
  let retriesLeft: number = retries;
  let token: () => void;
  lock.join((): void => isFunction(token) && token());

  const doRequest = (): Promise<Response<T>> => {
    return new Promise(
      (resolve: (response: Response<T> | Promise<Response<T>>) => any, reject: (response: Response<T>) => any) => {
        const req: XMLHttpRequest = new XMLHttpRequest();
        token = (): void => {
          req.abort();
          reject({ status: Status.CANCELED });
        };

        const retry = (): void => {
          if (retriesLeft > 0) {
            resolve(doRequest());
            retriesLeft--;
          } else {
            reject({ status: Status.ERROR });
          }
        };

        req.open(method, encodeURI(url + (isPresent(params) ? params.toString() : '')));
        if (isPresent(timeout)) {
          req.timeout = timeout;
        }
        if (withCredentials) {
          req.withCredentials = true;
        }
        if (isPresent(headers)) {
          Object.keys(headers).forEach((name: string) => req.setRequestHeader(name, headers[name]));
        }

        req.onload = (): void => {
          if (req.status >= 200 && req.status <= 308) {
            resolve({
              status: Status.OK,
              payload: headers.Accept === 'application/json' ? JSON.parse(req.response) : req.response,
            });
          } else {
            retry();
          }
        };
        req.ontimeout = (): void => retry();
        req.onerror = (): void => retry();

        req.send(body);
      }
    );
  };
  return doRequest();
};
